import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { toast } from 'sonner';
import { DashboardLayout } from './Dashboard';
import Tour from '../components/Tour';
import NotionExportButton from '../components/NotionExportButton';
import TrelloExportButton from '../components/TrelloExportButton';
import ClickUpExportButton from '../components/ClickUpExportButton';
import FunnelFlowchart, { competitorAnalysisToStages } from '../components/FunnelFlowchart';
import {
  Crosshair, Plus, ArrowsClockwise, Trash, Sparkle, Target, Warning, Check,
  Lightning, Trophy, MagnifyingGlass, Download, Copy, ArrowRight, Bell, BellSlash,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SAT_COLOR = { high: '#EF4444', medium: '#F59E0B', low: '#10B981' };
const STR_COLOR = { high: '#10B981', medium: '#F59E0B', low: '#A0A0A0' };

const copyToClipboard = async (text, label) => {
  try { await navigator.clipboard.writeText(text); toast.success(label || i18n.t('common.copied')); }
  catch { toast.error(i18n.t('common.copy_fail')); }
};

const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const SectionCard = ({ icon: Icon, title, color = '#A855F7', children, right }) => (
  <div className="p-5 bg-[#121212] border border-white/10 rounded-2xl">
    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}22` }}>
          <Icon size={16} weight="bold" style={{ color }} />
        </div>
        <h3 className="font-[Outfit] font-bold">{title}</h3>
      </div>
      {right}
    </div>
    {children}
  </div>
);

// ─── Watchlist Item Card ──────────────────────────────────────────

const WatchCard = ({ w, selected, onToggle, onRescan, onDelete, onToggleMonitor, busy }) => {
  const { t } = useTranslation();
  const snap = w.current_snapshot || {};
  const changed = !!w.last_change_summary;
  const unreachable = !!snap.unreachable;
  const monitoring = !!w.monitor_enabled;
  const host = (() => { try { return new URL(w.url).hostname.replace(/^www\./, ''); } catch { return w.url; } })();
  return (
    <div className={`p-4 rounded-xl border transition cursor-pointer ${selected ? 'bg-[#2563EB]/12 border-[#2563EB]/50' : 'bg-[#0A0A0A] border-white/10 hover:border-white/20'}`}
      onClick={() => onToggle(w.watch_id)} data-testid={`watch-card-${w.watch_id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-5 h-5 mt-0.5 rounded border-2 flex-shrink-0 flex items-center justify-center ${selected ? 'bg-[#2563EB] border-[#2563EB]' : 'border-white/30'}`}>
          {selected && <Check size={12} weight="bold" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="font-[Outfit] font-bold text-base truncate">{w.label}</div>
            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <button data-testid={`btn-monitor-${w.watch_id}`}
                onClick={() => onToggleMonitor(w.watch_id, !monitoring)}
                className={`p-1.5 rounded transition ${monitoring ? 'bg-[#A855F7]/20 text-[#A855F7] hover:bg-[#A855F7]/30' : 'text-white/40 hover:bg-white/10'}`}
                title={monitoring ? t('comp.mon_off_tip') : t('comp.mon_on_tip')}>
                {monitoring ? <Bell size={14} weight="fill" /> : <BellSlash size={14} />}
              </button>
              <button data-testid={`btn-rescan-${w.watch_id}`} onClick={() => onRescan(w.watch_id)} disabled={busy}
                className="p-1.5 rounded hover:bg-white/10 disabled:opacity-50" title={t('comp.rescan_tip')}>
                <ArrowsClockwise size={14} className={busy ? 'animate-spin' : ''} />
              </button>
              <button data-testid={`btn-delete-watch-${w.watch_id}`} onClick={() => onDelete(w.watch_id)}
                className="p-1.5 rounded text-white/40 hover:text-[#EF4444] hover:bg-[#EF4444]/10" title={t('comp.remove_tip')}>
                <Trash size={14} />
              </button>
            </div>
          </div>
          <a href={w.url} target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-white/50 hover:text-[#2563EB] truncate block">{host}</a>
          {snap.description && !unreachable && <p className="text-xs text-white/60 mt-1 line-clamp-2">{snap.description}</p>}
          <div className="flex items-center gap-2 mt-2 text-[10px] uppercase tracking-wider flex-wrap">
            <span className="text-white/40">scan {new Date(w.last_scanned_at).toLocaleDateString()}</span>
            {monitoring && (
              <span className="px-1.5 py-0.5 rounded bg-[#A855F7]/15 text-[#A855F7] font-bold flex items-center gap-1">
                <Bell size={10} weight="fill" /> auto
              </span>
            )}
            {unreachable && (
              <span className="px-1.5 py-0.5 rounded bg-[#EF4444]/15 text-[#EF4444] font-bold flex items-center gap-1">
                <Warning size={10} weight="bold" /> {t('comp.unreachable')}
              </span>
            )}
            {changed && !unreachable && (
              <span className="px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B] font-bold flex items-center gap-1">
                <Lightning size={10} weight="bold" /> {t('comp.changed')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Analysis View ────────────────────────────────────────────────

const AnalysisView = ({ analysis }) => {
  const { t } = useTranslation();
  if (!analysis) return null;
  const a = analysis;
  return (
    <div className="space-y-4">
      {a.executive_summary && (
        <div className="p-4 bg-gradient-to-br from-[#A855F7]/12 to-transparent border border-[#A855F7]/35 rounded-2xl">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A855F7] font-semibold mb-2">{t('comp.exec_summary')}</div>
          <p className="text-white/90 leading-relaxed">{a.executive_summary}</p>
        </div>
      )}

      {Array.isArray(a.common_themes) && a.common_themes.length > 0 && (
        <SectionCard icon={MagnifyingGlass} title={t('comp.common_themes')} color="#F59E0B">
          <div className="space-y-2">
            {a.common_themes.map((th, i) => (
              <div key={i} className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-semibold text-sm">{th.theme}</div>
                  {th.saturation && (
                    <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold"
                      style={{ color: SAT_COLOR[th.saturation] || '#A0A0A0', background: `${SAT_COLOR[th.saturation] || '#A0A0A0'}22` }}>
                      sat {th.saturation}
                    </span>
                  )}
                </div>
                {th.evidence && <p className="text-xs text-white/60 italic">&ldquo;{th.evidence}&rdquo;</p>}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {a.pricing_patterns && (
        <SectionCard icon={Target} title={t('comp.pricing_patterns')} color="#2563EB">
          <div className="space-y-2">
            {Array.isArray(a.pricing_patterns.observed_models) && a.pricing_patterns.observed_models.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {a.pricing_patterns.observed_models.map((m, i) => (
                  <span key={i} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded text-xs font-mono">{m}</span>
                ))}
              </div>
            )}
            {a.pricing_patterns.average_price_range_eur && (
              <div className="text-sm text-white/80">
                <span className="text-white/50">{t('comp.avg_range')}</span>{' '}
                <strong>€{a.pricing_patterns.average_price_range_eur.min}-€{a.pricing_patterns.average_price_range_eur.max}</strong>
              </div>
            )}
            {a.pricing_patterns.notes && <p className="text-sm text-white/70">{a.pricing_patterns.notes}</p>}
          </div>
        </SectionCard>
      )}

      {Array.isArray(a.unique_angles) && a.unique_angles.length > 0 && (
        <SectionCard icon={Trophy} title={t('comp.unique_angles')} color="#10B981">
          <div className="space-y-2">
            {a.unique_angles.map((u, i) => (
              <div key={i} className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-[Outfit] font-bold text-sm">{u.competitor}</div>
                  {u.strength && (
                    <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold"
                      style={{ color: STR_COLOR[u.strength] || '#A0A0A0', background: `${STR_COLOR[u.strength] || '#A0A0A0'}22` }}>
                      {u.strength}
                    </span>
                  )}
                </div>
                <p className="text-sm text-white/80">{u.owns}</p>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {Array.isArray(a.white_space_gaps) && a.white_space_gaps.length > 0 && (
        <SectionCard icon={Sparkle} title={t('comp.market_gaps')} color="#A855F7">
          <div className="space-y-3">
            {a.white_space_gaps.map((g, i) => (
              <div key={i} className="p-4 bg-gradient-to-br from-[#A855F7]/10 to-transparent border border-[#A855F7]/30 rounded-xl">
                <div className="font-[Outfit] font-bold text-base mb-2">{g.gap}</div>
                {g.why_it_works && (
                  <div className="text-xs mb-2">
                    <span className="text-[#10B981] font-semibold uppercase tracking-wider">{t('comp.why_works')}</span>
                    <span className="text-white/80">{g.why_it_works}</span>
                  </div>
                )}
                {g.attack_plan && (
                  <div className="p-2 bg-white/5 border-l-2 border-[#A855F7] rounded-r text-xs">
                    <div className="text-[#A855F7] font-semibold uppercase tracking-wider mb-0.5">{t('comp.attack_plan')}</div>
                    <p className="text-white/85">{g.attack_plan}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {a.differentiation_strategy && (
        <SectionCard icon={Crosshair} title={t('comp.diff_strategy')} color="#EF4444">
          <div className="space-y-3">
            {a.differentiation_strategy.positioning_statement && (
              <div className="p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg">
                <div className="text-[10px] uppercase tracking-wider text-[#EF4444] font-bold mb-1">{t('comp.positioning')}</div>
                <p className="text-sm font-semibold text-white">{a.differentiation_strategy.positioning_statement}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {Array.isArray(a.differentiation_strategy.key_messages) && (
                <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
                  <div className="text-[10px] uppercase tracking-wider text-[#2563EB] font-bold mb-1">{t('comp.key_messages')}</div>
                  <ul className="text-xs text-white/80 space-y-1 list-disc list-inside">
                    {a.differentiation_strategy.key_messages.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              )}
              {Array.isArray(a.differentiation_strategy.lean_into) && (
                <div className="p-3 bg-[#10B981]/8 border border-[#10B981]/25 rounded-lg">
                  <div className="text-[10px] uppercase tracking-wider text-[#10B981] font-bold mb-1">{t('comp.amplify')}</div>
                  <ul className="text-xs text-white/80 space-y-1 list-disc list-inside">
                    {a.differentiation_strategy.lean_into.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              )}
              {Array.isArray(a.differentiation_strategy.avoid) && (
                <div className="p-3 bg-[#EF4444]/8 border border-[#EF4444]/25 rounded-lg">
                  <div className="text-[10px] uppercase tracking-wider text-[#EF4444] font-bold mb-1">{t('comp.avoid')}</div>
                  <ul className="text-xs text-white/80 space-y-1 list-disc list-inside">
                    {a.differentiation_strategy.avoid.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </SectionCard>
      )}

      {Array.isArray(a.quick_wins) && a.quick_wins.length > 0 && (
        <SectionCard icon={Lightning} title={t('comp.quick_wins')} color="#10B981">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {a.quick_wins.map((q, i) => (
              <div key={i} className="p-3 bg-[#10B981]/8 border border-[#10B981]/25 rounded-lg text-sm flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-[#10B981]/20 flex items-center justify-center text-[10px] font-bold text-[#10B981] flex-shrink-0">{i + 1}</span>
                <span className="text-white/85">{q}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────

export default function Competitors() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [watchlist, setWatchlist] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [newUrl, setNewUrl] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [rescanBusy, setRescanBusy] = useState(new Set());
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [language, setLanguage] = useState('pt');
  const [focus, setFocus] = useState('');
  const [analyses, setAnalyses] = useState([]);

  const loadWatchlist = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/competitive-intel/watchlist`);
      setWatchlist(data.items || []);
    } catch { /* silent */ }
  }, []);

  const loadAnalyses = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/competitive-intel/analyses`);
      setAnalyses(data.items || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadWatchlist(); loadAnalyses(); }, [loadWatchlist, loadAnalyses]);
  useEffect(() => {
    const h = () => { loadWatchlist(); loadAnalyses(); };
    window.addEventListener('brand:changed', h);
    return () => window.removeEventListener('brand:changed', h);
  }, [loadWatchlist, loadAnalyses]);

  const addWatch = async (e) => {
    e?.preventDefault?.();
    const url = newUrl.trim();
    if (!url) { toast.error(t('comp.err_url')); return; }
    setAdding(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/competitive-intel/watch`, {
        url, label: newLabel.trim() || null,
      });
      setNewUrl(''); setNewLabel('');
      if (data.scrape_warning) {
        toast.warning(data.scrape_warning);
      } else {
        toast.success(data.reused ? t('comp.already_watch') : t('comp.added'));
      }
      loadWatchlist();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('comp.add_fail'));
    } finally { setAdding(false); }
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const rescan = async (id) => {
    setRescanBusy((s) => new Set(s).add(id));
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/competitive-intel/watch/${id}/rescan`);
      toast.success(data.had_changes ? t('comp.changes_yes') : t('comp.changes_no'));
      loadWatchlist();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('comp.rescan_fail'));
    } finally {
      setRescanBusy((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  };

  const deleteWatch = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/competitive-intel/watch/${id}`);
      setWatchlist((w) => w.filter((x) => x.watch_id !== id));
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
      toast.success(t('comp.removed'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('comp.error'));
    }
  };

  const toggleMonitor = async (id, enabled) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/competitive-intel/watch/${id}/monitor`, { enabled });
      setWatchlist((w) => w.map((x) => x.watch_id === id ? { ...x, monitor_enabled: enabled } : x));
      toast.success(enabled ? t('comp.monitor_on') : t('comp.monitor_off'));
    } catch (err) {
      const status = err.response?.status;
      if (status === 402) {
        toast.error(err.response?.data?.detail || t('comp.pro_feature'));
        setTimeout(() => navigate('/pricing?utm_source=monitor_upsell'), 1500);
      } else {
        toast.error(err.response?.data?.detail || t('comp.error'));
      }
    }
  };

  const analyze = async () => {
    const ids = Array.from(selected);
    if (ids.length < 2) { toast.error(t('comp.min_2')); return; }
    if (ids.length > 8) { toast.error(t('comp.max_8')); return; }
    const startedAt = Date.now();
    setAnalyzing(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/competitive-intel/analyze`, {
        watch_ids: ids, language, focus: focus || null,
      }, { timeout: 120000 });
      setResult(data);
      toast.success(t('comp.analysis_ready'));
      loadAnalyses();
    } catch (err) {
      const status = err.response?.status;
      const isTimeout = err.code === 'ECONNABORTED' || status === 502 || status === 504;
      if (isTimeout) {
        toast.info(t('comp.timeout_info'));
        const rec = await _pollForRecentAnalysis(ids, startedAt);
        if (rec) { setResult(rec); toast.success(t('comp.recovered')); loadAnalyses(); setAnalyzing(false); return; }
      }
      if (status === 429) {
        toast.error(err.response?.data?.detail || t('comp.limit'));
        setTimeout(() => navigate('/pricing?utm_source=competitive_quota'), 1500);
      } else {
        toast.error(err.response?.data?.detail || t('comp.analysis_fail'));
      }
    } finally { setAnalyzing(false); }
  };

  const _pollForRecentAnalysis = async (ids, startedAt) => {
    const want = new Set(ids);
    for (let i = 0; i < 10; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/competitive-intel/analyses`);
        const items = data.items || [];
        const m = items.find((x) => new Date(x.created_at).getTime() >= startedAt - 2000);
        if (m) {
          const detail = await axios.get(`${BACKEND_URL}/api/competitive-intel/analyses/${m.analysis_id}`);
          // sanity check ids overlap
          const got = new Set(detail.data.watch_ids || []);
          if ([...want].every((x) => got.has(x))) return detail.data;
        }
      } catch { /* retry */ }
    }
    return null;
  };

  const loadAnalysis = async (id) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/competitive-intel/analyses/${id}`);
      setResult(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch { toast.error(t('comp.load_fail')); }
  };

  const deleteAnalysis = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/competitive-intel/analyses/${id}`);
      setAnalyses((a) => a.filter((x) => x.analysis_id !== id));
      if (result?.analysis_id === id) setResult(null);
      toast.success(t('comp.deleted'));
    } catch { toast.error(t('comp.error')); }
  };

  const canAnalyze = useMemo(() => selected.size >= 2 && selected.size <= 8 && !analyzing, [selected, analyzing]);

  return (
    <DashboardLayout>
      <Tour tourId="competitors-v1" steps={[
        { target: '[data-testid="input-competitor-url"]', content: t('comp.tour_1'), disableBeacon: true },
        { target: '[data-testid="btn-add-competitor"]', content: t('comp.tour_2') },
        { target: '[data-testid="btn-cross-analyze"]', content: t('comp.tour_3') },
      ]} />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-[Outfit] font-bold tracking-tight">Competitive Intelligence</h1>
          <p className="text-white/60 text-sm mt-1">{t('comp.subtitle')}</p>
        </div>

        {/* Add competitor form */}
        <form onSubmit={addWatch} className="p-5 bg-[#121212] border border-white/10 rounded-2xl">
          <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-3 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50">{t('comp.url_label')}</label>
              <input data-testid="input-competitor-url" type="url" value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)} required
                placeholder="https://concorrente.com/oferta"
                className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50">{t('comp.label_label')}</label>
              <input data-testid="input-competitor-label" value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder={t('comp.label_ph')}
                className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]" />
            </div>
            <button data-testid="btn-add-competitor" type="submit" disabled={adding}
              className="px-5 py-2 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] rounded-lg font-semibold text-sm hover:opacity-90 transition disabled:opacity-50 flex items-center gap-2">
              <Plus size={14} weight="bold" /> {adding ? t('comp.adding') : t('comp.add')}
            </button>
          </div>
        </form>

        {/* Watchlist */}
        {watchlist.length > 0 && (
          <SectionCard icon={Crosshair} title={`Watchlist · ${watchlist.length}`} color="#2563EB"
            right={
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-white/50">{t('comp.selected', { n: selected.size })}</span>
                <button data-testid="btn-cross-analyze"
                  onClick={analyze} disabled={!canAnalyze}
                  className="px-4 py-2 bg-gradient-to-r from-[#A855F7] to-[#EF4444] rounded-lg font-bold text-xs text-white hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2">
                  {analyzing ? <><Sparkle size={12} weight="bold" className="animate-pulse" /> {t('comp.analyzing')}</> : <><Sparkle size={12} weight="bold" /> {t('comp.cross_analyze')}</>}
                </button>
              </div>
            }>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {watchlist.map((w) => (
                <WatchCard key={w.watch_id} w={w}
                  selected={selected.has(w.watch_id)}
                  onToggle={toggleSelect}
                  onRescan={rescan}
                  onDelete={deleteWatch}
                  onToggleMonitor={toggleMonitor}
                  busy={rescanBusy.has(w.watch_id)} />
              ))}
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 pt-4 border-t border-white/5">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/50">{t('comp.analysis_lang')}</label>
                <select data-testid="select-language" value={language} onChange={(e) => setLanguage(e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]">
                  <option value="pt">Português</option>
                  <option value="es">Español</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-white/50">{t('comp.focus_label')}</label>
                <input data-testid="input-focus" value={focus} onChange={(e) => setFocus(e.target.value)}
                  placeholder={t('comp.focus_ph')}
                  className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]" />
              </div>
            </div>
          </SectionCard>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-4" data-testid="analysis-result">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                {t('comp.analysis_of')} · {(result.competitor_labels || []).join(' · ')}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button data-testid="btn-export-analysis"
                  onClick={() => downloadJson(result, `analysis-${result.analysis_id}.json`)}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs font-semibold hover:bg-white/10 flex items-center gap-1.5">
                  <Download size={12} weight="bold" /> JSON
                </button>
                <NotionExportButton
                  shape_type="competitor_analysis"
                  source_id={result.analysis_id}
                  testId="btn-export-notion-competitors"
                />
                <TrelloExportButton
                  shape_type="competitor_analysis"
                  source_id={result.analysis_id}
                  testId="btn-export-trello-competitors"
                />
                <ClickUpExportButton
                  shape_type="competitor_analysis"
                  source_id={result.analysis_id}
                  testId="btn-export-clickup-competitors"
                />
              </div>
            </div>
            <FunnelFlowchart
              stages={competitorAnalysisToStages(result)}
              title={t('comp.flowchart_title')}
              height="420px"
              accent="#EF4444"
              downloadName={`competitor-attack-${result.analysis_id}`}
              testId="competitors-flowchart"
            />
            <AnalysisView analysis={result.analysis} />
          </div>
        )}

        {/* Past analyses */}
        {analyses.length > 0 && (
          <SectionCard icon={Trophy} title={t('comp.past_analyses')} color="#666">
            <div className="space-y-2">
              {analyses.map((a) => (
                <div key={a.analysis_id} className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg flex items-center justify-between gap-2">
                  <button data-testid={`analysis-item-${a.analysis_id}`}
                    onClick={() => loadAnalysis(a.analysis_id)}
                    className="flex-1 text-left hover:opacity-80 transition">
                    <div className="font-[Outfit] font-bold text-sm truncate">{(a.competitor_labels || []).join(' · ')}</div>
                    <div className="text-xs text-white/50 mt-0.5">
                      {a.focus && <span className="text-[#A855F7]">{a.focus} · </span>}
                      {new Date(a.created_at).toLocaleDateString()}
                    </div>
                  </button>
                  <button data-testid={`analysis-delete-${a.analysis_id}`}
                    onClick={() => deleteAnalysis(a.analysis_id)}
                    className="p-1.5 rounded text-white/40 hover:text-[#EF4444] hover:bg-[#EF4444]/10">
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {!result && watchlist.length === 0 && (
          <div className="p-8 bg-[#121212] border border-dashed border-white/15 rounded-2xl text-center">
            <Crosshair size={32} weight="bold" className="text-[#A855F7] mx-auto mb-3" />
            <h3 className="font-[Outfit] font-bold text-lg mb-1">{t('comp.empty_title')}</h3>
            <p className="text-sm text-white/60 max-w-lg mx-auto">{t('comp.empty_sub')}</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
